import React, { useState, FC, useRef, RefObject, useEffect } from 'react'
import { Button, TextField, Dialog, DialogActions, DialogContent, IconButton, DialogTitle, Paper, PaperProps, FormHelperText, InputAdornment, Alert, AlertTitle, Box, Grid } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { AuthService } from 'services/AuthService'
import { AxiosError } from 'axios'
import { useSnackbar } from 'notistack'
import Draggable from 'react-draggable';
import CloseIcon from '@mui/icons-material/Close';
import { nameValidation } from 'utils/Validations'

interface AddProfileProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    select: any;
    rows: any;
    reloading: () => void
}

function PaperComponent(props: PaperProps) {
    const nodeRef = useRef<HTMLDivElement>(null);
    return (
        <Draggable
            nodeRef={nodeRef as RefObject<HTMLDivElement>}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

const EditProfile: FC<AddProfileProps> = ({ open, setOpen, select, rows, reloading }) => {
    const [name, setName] = useState('')
    const [nameLength, setNameLength] = useState(0)
    const [alerts, setAlerts] = useState(false)
    const [loading, setLoading] = useState(false)


    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const authService = new AuthService()

    useEffect(() => {
        if (open) {
            const row = rows.find((row: any) => row.id === select[0].id)
            setName(row.name)
        }
    }, [open, rows, select])

    const existName = (name: string) => {
        // Si el nombre no ha cambiado del original, no debe considerarse como existente
        if (select[0] && String(select[0].name).toLowerCase() === String(name).toLowerCase().trim()) {
            return false;
        }

        // Verifica si existe en otros registros
        return rows.some((row: any) => String(row.name).toLowerCase() === String(name).toLowerCase().trim());
    }

    const closeForm = () => {
        setOpen(false)
        setName('')
        setNameLength(0)
        setAlerts(false)
    }

    const changes = () => {
        return name !== '' ? setAlerts(true) : closeForm()
    }

    const UpdateProfile = async () => {
        setLoading(true);
        try {
            await authService.profile.updateProfile({ profile_id: select[0].id, name: name });

            enqueueSnackbar(t('profiles.table.dialog.edit.messages.success'), { variant: 'success' });
            reloading();
            setOpen(false);
        } catch (error: unknown) {
            const axiosError = error as AxiosError;
            enqueueSnackbar(axiosError.message, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const disabled = () => {
        return name === '' || existName(name) || nameLength === 0
    }

    return (
        <Dialog
            open={open}
            onClose={() => changes()}
            fullWidth
            maxWidth="xs"
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">{t('profiles.table.dialog.edit.title')}</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => changes()}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>
                {
                    alerts &&
                    <Alert
                        variant='filled'
                        severity="warning"
                        sx={{ mt: 2 }}
                        action={
                            <>
                                <Button onClick={() => closeForm()} color="inherit" size="small" variant='text' disableElevation>
                                    {t('custom_reports.category.table.dialog.add.alert.unsaved_changes.buttons.yes')}
                                </Button>
                                <Button onClick={() => setAlerts(false)} color='inherit' size='small' variant='text' disableElevation>
                                    {t('custom_reports.category.table.dialog.add.alert.unsaved_changes.buttons.no')}
                                </Button>
                            </>
                        }
                    >
                        <AlertTitle>{t('custom_reports.category.table.dialog.add.alert.unsaved_changes.message')}</AlertTitle>
                    </Alert>
                }
            </Box>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <TextField
                            label={t('profiles.table.dialog.add.name')}
                            variant="outlined"
                            fullWidth
                            autoComplete='off'
                            value={name}
                            onChange={(e) => {

                                const newName = e.target.value
                                if (nameValidation(newName)) {
                                    setName(newName)
                                    existName(newName)
                                    setNameLength(newName.length)
                                }

                            }}
                            error={existName(name)}
                            required
                            slotProps={{
                                htmlInput: { maxLength: 100 },
                                input: {
                                    endAdornment: <InputAdornment position="end">{`${nameLength}/100`}</InputAdornment>,
                                },
                            }}
                        />
                        <FormHelperText
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                mt: 0.5,
                                px: 1.75
                            }}
                        >
                            <span>{existName(name) ? t('custom_reports.category.table.dialog.add.exist_name') : ''}</span>
                        </FormHelperText>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={() => changes()} color="error">
                    {t('profiles.table.dialog.add.buttons.cancel')}
                </Button>
                <Button variant='contained' onClick={() => UpdateProfile()} color="primary" disabled={disabled()} loading={loading}>
                    {t('profiles.table.dialog.edit.buttons.edit')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
export { EditProfile }
